import { NO_INTEGRATION_CREATE_BUTTON_KEY } from "@/const/ticketing.constants"
import { getStepsValues } from "@/helpers/form.helper"

import type {
  Action,
  ActionValues,
  TicketActionType,
  Workflow
} from "@/api/useWorkflow.types"
import type { VendorTicketingSystem } from "@/domain/ticketing"
import type { Permission } from "@/domain/user"
import { buildCreateIssuePayload } from "@/helpers/jira.helpers"

export const TICKET_ACTION_SYSTEMS: Record<
  TicketActionType,
  VendorTicketingSystem
> = {
  azure_boards_issue: "azure_boards",
  asana_task: "asana",
  github_issue: "github"
}

export const TICKET_ACTION_VALUE_KEYS: Record<
  TicketActionType,
  "azureBoardsIssue" | "asanaTask" | "githubIssue"
> = {
  azure_boards_issue: "azureBoardsIssue",
  asana_task: "asanaTask",
  github_issue: "githubIssue"
}

export const isTicketActionType = (type: unknown): type is TicketActionType =>
  Object.keys(TICKET_ACTION_SYSTEMS).some((key) => key === type)

// The form keeps a vendor's template under ticketIssue, already in the API's shape
const toTicketActionValues = (
  type: TicketActionType,
  ticketIssue: ActionValues["asanaTask"]
): ActionValues => ({ [TICKET_ACTION_VALUE_KEYS[type]]: ticketIssue })

export const ALL_SCOPE = "all"
export const SPECIFIC_SCOPE = "specific"

export const getInitialValues = (data: Workflow | undefined) => {
  const {
    name,
    description,
    trigger,
    actions = [],
    resourceConditions = [],
    filterConditions = []
  } = data || {}
  const { category, type: triggerType } = trigger || {}
  const isResourceConditions = !!resourceConditions.length
  const transformedActions = transformActionsToInitialValues(actions)
  return {
    "details.name": name,
    "details.description": description,
    "trigger.category": category,
    "trigger.type": triggerType,
    actions: transformedActions.length == 0 ? [{}] : transformedActions,
    conditions: isResourceConditions
      ? resourceConditions
      : [{ filters: [{}], includeExclude: "Include" }],
    currentConditions: getStepsValues(filterConditions),
    currentScope: isResourceConditions ? SPECIFIC_SCOPE : ALL_SCOPE
  }
}

export const getResetFieldsValuesByTriggerKey = (
  key: string,
  state: Record<string, Record<string, unknown>>
) => {
  const defaultState = {
    currentConditions: getStepsValues([])
  }
  const prevState = state[key]
  const nextState = prevState || defaultState

  return {
    currentConditions: nextState.currentConditions
  }
}

const transformActionsToInitialValues = (actions: Action[]) => {
  return actions.map((action) => {
    switch (action.type) {
      case "webhook":
        return {
          type: action.type,
          webhooks: action.values.webhookIds
        }
      case "label":
        return {
          type: action.type,
          labels: action.values.labelIds
        }
      case "dismiss":
        return {
          type: action.type,
          reason: action.values.reason,
          undismissOnKaiConfirmed: action.values.undismissOnKaiConfirmed
        }
      case "reopen":
        return {
          type: action.type,
          reason: action.values.reason
        }
      case "jira_issue":
        return {
          type: action.type,
          jiraIssue: action.values.jiraIssue,
          relatedError: action.relatedError
        }
      case "slack_message":
        return {
          type: action.type,
          slackChannels: action.values.slackChannelIds,
          messageTemplate: action.values.messageTemplate
        }
      case "azure_boards_issue":
      case "asana_task":
      case "github_issue":
        return {
          type: action.type,
          ticketIssue: action.values[TICKET_ACTION_VALUE_KEYS[action.type]],
          relatedError: action.relatedError
        }
    }
  })
}

export const usesKaiCondition = (
  steps:
    ({ value?: string; selectedValue?: unknown } | undefined)[][] | undefined
) =>
  !!steps?.some((items) =>
    items?.some(
      (item) =>
        item?.value === "kaiMarkedAsFalsePositive" &&
        String(item?.selectedValue).toUpperCase() === "YES"
    )
  )

export const transformActionsToPayload = (actions: any[]) => {
  return actions.map((action) => {
    let values = {}

    switch (action.type) {
      case "webhook":
        values = { webhookIds: action.webhooks }
        break
      case "label":
        values = { labelIds: action.labels }
        break
      case "dismiss":
        values = {
          reason: action.reason,
          undismissOnKaiConfirmed: action.undismissOnKaiConfirmed
        }
        break
      case "reopen":
        values = { reason: action.reason }
        break
      case "jira_issue": {
        const fields = action.jiraFieldsMetadata
          ? buildCreateIssuePayload({
              fieldsMetadata: action.jiraFieldsMetadata,
              fieldsValues: action.jiraIssue.fields
            }).jiraIssueFields.fields
          : action.jiraIssue.fields

        values = {
          jiraIssue: {
            fields,
            onDemandFields: action.jiraIssue.onDemandFields
          }
        }
        break
      }
      case "slack_message":
        values = {
          slackChannelIds: action.slackChannels,
          messageTemplate: action.messageTemplate || ""
        }
        break
      case "azure_boards_issue":
      case "asana_task":
      case "github_issue":
        values = toTicketActionValues(action.type, action.ticketIssue)
        break
    }

    return {
      type: action.type,
      values
    }
  })
}

// The create-ticket button labels itself for a template: create until one exists
export const getTemplateButtonT =
  (attr: {
    t: (key: string) => string
    buttonKey: string
    isNew: boolean
    createLabel: string
    editLabel: string
  }) =>
  (key: string) => {
    const { t, buttonKey, isNew, createLabel, editLabel } = attr

    if (key === buttonKey) return isNew ? createLabel : editLabel
    if (key === NO_INTEGRATION_CREATE_BUTTON_KEY) return createLabel

    return t(key)
  }

export const getWritePermission = (): Permission => ({
  resource: "governance.workflows",
  action: "write"
})
